import { Utensils } from "lucide-react"
import { cn } from "@/lib/utils"

type DishIllustrationProps = {
  src?: string | null
  /** Staff-assigned emoji (future). Shown when there is no photo. */
  emoji?: string | null
  alt?: string
  className?: string
  iconClassName?: string
  emojiClassName?: string
  /** @deprecated legacy gradient variant — ignored under the neutral direction. */
  variant?: number
  /** @deprecated legacy initials glyph — ignored under the neutral direction. */
  glyph?: string
}

/**
 * Dish visual with a fixed precedence: photo → staff emoji → neutral icon fallback.
 * The emoji is a future staff-assigned field; today most items fall back to the icon.
 */
export function DishIllustration({
  src,
  emoji,
  alt = "",
  className,
  iconClassName,
  emojiClassName,
}: DishIllustrationProps) {
  if (src) {
    return (
      <div className={cn("relative size-full overflow-hidden bg-muted", className)}>
        <img src={src} alt={alt} className="absolute inset-0 size-full object-cover" />
      </div>
    )
  }

  if (emoji) {
    return (
      <div
        className={cn("flex size-full items-center justify-center bg-muted", className)}
        aria-hidden="true"
      >
        <span className={cn("text-[1.6em] leading-none", emojiClassName)}>{emoji}</span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex size-full items-center justify-center bg-muted text-muted-foreground",
        className
      )}
      aria-hidden="true"
    >
      <Utensils className={cn("size-6", iconClassName)} strokeWidth={1.75} />
    </div>
  )
}

export function variantForId(id: number | string) {
  const seed =
    typeof id === "string"
      ? Array.from(id).reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
      : Number(id)
  return ((seed % 6) + 1) as 1 | 2 | 3 | 4 | 5 | 6
}

export function glyphFor(name: string) {
  const trimmed = name.trim()
  if (!trimmed) return "?"
  const words = trimmed.split(/\s+/).filter(Boolean)
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
